/* eslint-disable class-methods-use-this */

import { ProposalDAOInterface, ProposalTextsDAOInterface } from '../ProposalDAO';
import BaseTextDAO from './BaseTextDAO';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { getPortfoliosForView, calculateTotalPortfolioValue, getPortfolioValue } from '../../utils/client-level-summary';
import { formatCurrency, formatLocalizedString } from '@/utils/i18n';
import { PROPOSAL_ORIENTATION } from '../../utils/constants';
import { CLIENT_LEVEL_VIEW } from '@/enums/ClientLevelView';

/**
 * Unified config type for PortfolioValueBreakdownDAO
 */
type PortfolioValueBreakdownConfig = {
  view: CLIENT_LEVEL_VIEW;
  title: any;
};

/**
 * PortfolioValueBreakdownDAO
 *
 * Unified DAO for rendering the complete portfolio value breakdown section.
 * Generates both:
 * 1. Left section: Total portfolio value with grey background
 * 2. Right section: Grid of individual portfolio cards
 *
 * Supports both CURRENT and RECOMMENDED views with portrait/landscape orientations.
 */
export default class PortfolioValueBreakdownDAO extends BaseTextDAO implements ProposalTextsDAOInterface {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new PortfolioValueBreakdownDAO(proposal, proposalElement);
  }

  private view = CLIENT_LEVEL_VIEW.CURRENT;

  private title: any;

  protected getColorKeys(): string[] {
    return [];
  }

  protected build(): void {
    const daoConfig: PortfolioValueBreakdownConfig = this.proposalElement.DAO as PortfolioValueBreakdownConfig;

    this.view = daoConfig.view;
    this.title = daoConfig.title;
  }

  getTexts(): ProposalNS.ProposalText[] {
    const out: ProposalNS.ProposalText[] = [];

    // Get portfolios for the specified view
    const portfolios = getPortfoliosForView(this.proposal, this.view);

    // Calculate total value of all portfolios
    const totalValue = calculateTotalPortfolioValue(portfolios);

    const formattedTotal = formatCurrency(totalValue, 2, undefined, true, this.proposal.options.locale);

    const { locale, orientation } = this.proposal.options;

    const maxColumnsPerRow = orientation === PROPOSAL_ORIENTATION.PORTRAIT ? 2 : 3;
    const numRows = Math.ceil(portfolios.length / maxColumnsPerRow);

    const currentOrientation = orientation.toLowerCase();

    const portfolioCards = portfolios
      .map((portfolio, index) => {
        const portfolioValue = getPortfolioValue(portfolio);
        const formattedValue = formatCurrency(portfolioValue, 2, undefined, true, locale);

        return `
          <div class="portfolio-card" data-portfolio-index="${index}">
            <div class="portfolio-card-name">${formatLocalizedString(portfolio.name, locale)}</div>
            <div class="portfolio-card-value">${formattedValue}</div>
          </div>
        `;
      })
      .join('');

    const htmlContent = `
      <div class="total-portfolio-value">
        <div class="total-value-wrapper ${currentOrientation}">
          <div class="total-value-container ${currentOrientation}">
            <div class="total-value-label">${formatLocalizedString(this.title, locale)}</div>
            <div class="total-value-amount">${formattedTotal}</div>
          </div>
        </div>
      </div>
      <div class="portfolio-value-breakdown-grid">
        <div class="breakdown-cards-wrapper">
          <div class="breakdown-cards-container ${currentOrientation}" style="--max-columns: ${maxColumnsPerRow}; --num-rows: ${numRows}">
            ${portfolioCards}
          </div>
        </div>
      </div>
    `;

    out.push({
      text: htmlContent,
      className: 'portfolio-value-breakdown-container',
    });

    return out;
  }

  getData(): unknown[] {
    return [];
  }
}
